import type { ApolloClient } from '@apollo/client';
import {
    NetworkStatus,
    type ApolloQueryResult,
    type OperationVariables,
    type TypedDocumentNode,
    type WatchQueryOptions,
} from '@apollo/client/core/core.cjs';
import type { FetchMoreQueryOptions } from '@apollo/client/core/watchQueryOptions';
import type { DocumentNode } from 'graphql/index';
import { derived, get, type Readable, readable, readonly, writable } from 'svelte/store';
import type { EmptyVariables, WrapData } from '$lib/apollo/ApolloData.js';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { getContainer } from '$components/Context/ContainerContext';

export function writeQueryInCache(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queries: WrapData<any, any> | Array<WrapData<any, any>>,
    apolloClient: ApolloClient<unknown> | null = null,
) {
    const client = apolloClient != null ? apolloClient : getContainer().apollo();
    let it = Array.isArray(queries) ? queries : [queries];
    for (let query of it) {
        client.writeQuery(query);
    }
}

export type QueryInCacheAndObserveReturn<
    TData,
    TVariables extends OperationVariables = EmptyVariables,
> = {
    initialData: Readable<TData>;
    loading: Readable<boolean>;
    data: Readable<TData>;
    refetch: () => Promise<ApolloQueryResult<TData>>;
    fetchMore: FetchMore<TVariables, TData>;
    result: Readable<
        ApolloQueryResult<TData | null> & {
            prevData: TData | null;
            dataOrPrev: TData | null;
        }
    >;
    setOptions: (options: WrapData<TData, TVariables>) => void;
};

export function createQueryInCacheAndObserve<
    TData,
    TVariables extends OperationVariables,
>(
    wrap: WrapData<TData, TVariables>,
    options: Omit<
        WatchQueryOptions<TVariables, TData>,
        'query' | 'returnPartialData' | 'variables'
    > = {},
    container: Pick<ShareableContainer, 'browser' | 'apollo'> | null = null,
): QueryInCacheAndObserveReturn<TData, TVariables> {
    container = container ?? getContainer();
    const client = container.apollo();
    client.writeQuery(wrap);

    const { result, setVariables, fetchMore, refetch } = query(
        wrap.query,
        {
            fetchPolicy: 'cache-only',
            ...options,
            variables: wrap.variables,
        },
        container,
    );

    let initialData = writable(wrap.data);

    return {
        initialData: readonly(initialData),
        loading: derived(
            result,
            ($result, set) => {
                set($result.loading);
            },
            get(result).loading,
        ),
        data: derived(
            [result, initialData],
            ([$result, $initialData], set) => {
                if ($result.data == null) {
                    set($initialData);
                } else {
                    set($result.data);
                }
            },
            get(initialData),
        ),
        fetchMore,
        result,
        refetch,
        setOptions: ({
            query,
            data,
            variables,
        }: {
            query: DocumentNode | TypedDocumentNode<TData, TVariables>;
            data: TData;
            variables: TVariables;
        }) => {
            client.writeQuery({ query, data, variables });
            initialData.set(data);
            setVariables(variables).catch(console.error);
        },
    };
}

type FetchMore<TVariables, TData> = (
    fetchMoreOptions: FetchMoreQueryOptions<TVariables, TData> & {
        updateQuery?: (
            previousQueryResult: TData,
            options: {
                fetchMoreResult: TData;
                variables: TVariables;
            },
        ) => TData;
    },
) => Promise<ApolloQueryResult<TData>>;

export type QueryReturn<
    TData,
    TVariables extends OperationVariables,
    TEffectiveVariables extends TVariables = TVariables,
> = {
    variables: Readable<TEffectiveVariables>;
    setVariables: (
        variables: TEffectiveVariables,
    ) => Promise<ApolloQueryResult<TData> | void>;
    refetch: () => Promise<ApolloQueryResult<TData>>;
    fetchMore: FetchMore<TVariables, TData>;
    result: Readable<
        ApolloQueryResult<TData | null> & {
            prevData: TData | null;
            dataOrPrev: TData | null;
        }
    >;
};

export function query<
    TData,
    TVariables extends OperationVariables,
    TEffectiveVariables extends TVariables = TVariables,
>(
    query: TypedDocumentNode<TData, TVariables>,
    options: Omit<
        WatchQueryOptions<TEffectiveVariables, TData>,
        'query' | 'returnPartialData'
    > = {},
    container: Pick<ShareableContainer, 'browser' | 'apollo'> | null = null,
): QueryReturn<TData, TVariables, TEffectiveVariables> {
    let o = options as Omit<
        WatchQueryOptions<TVariables, TData>,
        'query' | 'returnPartialData'
    >;

    container = container ?? getContainer();
    const client = container.apollo();
    const browser = container.browser();

    const cachedResult = client.cache.readQuery({
        query,
        returnPartialData: false,
        variables: o['variables'] ?? undefined,
    });

    const initialResult = {
        data: cachedResult,
        prevData: cachedResult,
        dataOrPrev: cachedResult,
        loading: cachedResult == null,
        networkStatus: cachedResult != null ? NetworkStatus.ready : NetworkStatus.loading,
    };

    const variables = writable(options.variables);

    if (!browser) {
        return {
            variables: readonly(variables),
            setVariables: async (v) => {
                variables.set(v);
            },
            fetchMore: async () => {
                return new Promise((_resolve, reject) => {
                    reject(new Error('Unable to call fetchMore on server'));
                });
            },
            refetch: () => {
                return new Promise((_resolve, reject) => {
                    reject(new Error('Unable to call refetch on server'));
                });
            },
            result: readable<
                ApolloQueryResult<TData | null> & {
                    prevData: TData | null;
                    dataOrPrev: TData | null;
                }
            >(initialResult),
        };
    }

    const observable = client.watchQuery<TData, TVariables>({
        query,
        ...o,
        returnPartialData: false,
    });

    let prevData: TData | null = null;

    return {
        variables: readonly(variables),
        setVariables: (v) => {
            variables.set(v);
            return observable.setVariables(v);
        },
        fetchMore: observable.fetchMore.bind(observable),
        refetch: () => observable.refetch(),
        result: readable<
            ApolloQueryResult<TData | null> & {
                prevData: TData | null;
                dataOrPrev: TData | null;
            }
        >(initialResult, (set) => {
            const subscription = observable.subscribe((result) => {
                if (result.partial === true) {
                    set({
                        ...result,
                        data: null,
                        prevData,
                        dataOrPrev: prevData,
                    });
                } else {
                    set({
                        ...result,
                        prevData,
                        dataOrPrev: result.data == null ? prevData : result.data,
                    });
                    prevData = result.data;
                }
            });

            return () => subscription.unsubscribe();
        }),
    };
}
